/**
 * HerdDetail + MilkTab + FeedTab + HealthTab + EconomicsTab
 * Extracted from DetailPage.jsx (A12 Sprint 6).
 *
 * Tabbed herd management: Übersicht, Milch, Fütterung, Gesundheit, Wirtschaftlichkeit.
 * Includes herd selector for multi-herd farms.
 *
 * Co-extracted private helpers:
 *   - MilkTab (dairy milk record entry + yield chart)
 *   - FeedTab (feed ration management + cost calculator)
 *   - HealthTab (health metrics with inline editing)
 *   - EconomicsTab (DB I per-cow economics breakdown)
 *
 * A6-26 fix: raw fetch → apiClient.get (HerdDetail useEffect hooks + refresh functions)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../../lib/apiClient';
import SourceBadge from '../../components/SourceBadge';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// ─── Milk Tab ─────────────────────────────────────────────────
function MilkTab({ herdId, milkData, milkSummary, herdDetail, token, currency, t, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, avg_daily_yield_kg: '', milk_fat_pct: '', milk_protein_pct: '', somatic_cell_count: '', milk_price_ct_kg: '', total_delivered_kg: '' });
  const [saving, setSaving] = useState(false);

  const MONTHS = [t('months.jan'),t('months.feb'),t('months.mar'),t('months.apr'),t('months.may'),t('months.jun'),t('months.jul'),t('months.aug'),t('months.sep'),t('months.oct'),t('months.nov'),t('months.dec')];

  const handleSave = async () => {
    if (!token || !herdId) return;
    setSaving(true);
    const payload = { year: parseInt(form.year), month: parseInt(form.month) };
    if (form.avg_daily_yield_kg) payload.avg_daily_yield_kg = parseFloat(form.avg_daily_yield_kg);
    if (form.milk_fat_pct) payload.milk_fat_pct = parseFloat(form.milk_fat_pct);
    if (form.milk_protein_pct) payload.milk_protein_pct = parseFloat(form.milk_protein_pct);
    if (form.somatic_cell_count) payload.somatic_cell_count = parseInt(form.somatic_cell_count);
    if (form.milk_price_ct_kg) payload.milk_price_ct_kg = parseFloat(form.milk_price_ct_kg);
    if (form.total_delivered_kg) payload.total_delivered_kg = parseFloat(form.total_delivered_kg);

    try {
      await apiClient.post(`/api/v1/aif/farm/herds/${herdId}/milk`, payload, {
        entityType: 'milk_record', queueOnOffline: true,
      });
      setShowForm(false);
      onSaved?.();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const records = milkData?.records || [];
  const chartData = records.map(r => ({
    name: `${MONTHS[(r.month || 1) - 1]} ${r.year}`,
    yield: r.avg_daily_yield_kg,
    price: r.milk_price_ct_kg,
    fat: r.milk_fat_pct,
  })).filter(r => r.yield);

  const isDairy = herdDetail?.species === 'DAIRY_CATTLE';

  if (!isDairy) {
    return (
      <div className="text-center py-8 text-[var(--aif-text-muted)] text-xs">
        {t('herd.milkDairyOnly')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      {milkSummary?.has_data && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] p-3 text-center">
            <div className="text-xl font-bold text-[var(--aif-color-primary)]">{milkSummary.avg_daily_yield_kg}</div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkKgCowDay')}</div>
          </div>
          <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] p-3 text-center">
            <div className="text-xl font-bold text-[var(--aif-color-primary)]">{milkSummary.avg_milk_price_ct_kg}</div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkCtKg')}</div>
          </div>
          {milkSummary.annual_yield_kg_per_cow && (
            <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] p-3 text-center">
              <div className="text-lg font-bold text-[var(--aif-color-primary)]">{(milkSummary.annual_yield_kg_per_cow / 1000).toFixed(1)} t</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkAnnualYieldPerCow')}</div>
            </div>
          )}
          {milkSummary.annual_milk_revenue && (
            <div className="rounded-xl bg-green-500/10 border border-green-500/30 p-3 text-center">
              <div className="text-lg font-bold text-green-400">{milkSummary.annual_milk_revenue?.toLocaleString()}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkAnnualRevenue')} {currency}</div>
            </div>
          )}
        </div>
      )}

      {/* Yield chart */}
      {chartData.length >= 2 && (
        <div>
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('herd.milkYield')} ({t('herd.milkKgCowDay')})</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
                <XAxis dataKey="name" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} domain={['auto','auto']} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="yield" stroke="var(--aif-color-primary)" strokeWidth={2} dot={{ r: 3 }} name={t('herd.milkKgCowDay')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Add button */}
      <button
        onClick={() => setShowForm(!showForm)}
        className="w-full py-2 rounded-xl text-xs font-medium border border-dashed border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)] hover:bg-[var(--aif-color-primary)]/5"
      >
        + {t('herd.milkAddRecord')}
      </button>

      {/* Add form */}
      {showForm && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkYear')}</label>
              <input type="number" value={form.year} onChange={e => setForm({...form, year: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.milkMonth')}</label>
              <select value={form.month} onChange={e => setForm({...form, month: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
          </div>
          {[
            ['avg_daily_yield_kg', `${t('herd.milkYield')} (${t('herd.milkKgCowDay')})`, '28.5'],
            ['milk_fat_pct', `${t('herd.milkFat')} (%)`, '4.1'],
            ['milk_protein_pct', `${t('herd.milkProtein')} (%)`, '3.4'],
            ['milk_price_ct_kg', `${t('herd.milkPrice')} (ct/kg)`, '42.5'],
            ['total_delivered_kg', `${t('herd.milkDelivered')} (kg/Monat)`, ''],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{label}</label>
              <input type="number" step="0.1" placeholder={placeholder}
                value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium">
              {saving ? '...' : t('finance.saveValue')}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-muted)]">
              {t('finance.cancelEdit')}
            </button>
          </div>
        </div>
      )}

      {/* Records table */}
      {records.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
          <table className="w-full text-[11px] min-w-[350px]">
            <thead>
              <tr className="bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] uppercase text-[9px]">
                <th className="text-left p-2 pl-3">{t('herd.milkMonth')}</th>
                <th className="text-right p-2">{t('herd.milkKgDay')}</th>
                <th className="text-right p-2">{t('herd.milkFatPct')}</th>
                <th className="text-right p-2">{t('herd.milkCtKg')}</th>
              </tr>
            </thead>
            <tbody>
              {[...records].reverse().slice(0, 12).map((r, i) => (
                <tr key={i} className="border-t border-[var(--aif-border-default)]">
                  <td className="p-2 pl-3 text-[var(--aif-text-primary)]">{MONTHS[(r.month || 1) - 1]} {r.year}</td>
                  <td className="text-right p-2 text-[var(--aif-text-primary)]">{r.avg_daily_yield_kg ?? '—'}</td>
                  <td className="text-right p-2 text-[var(--aif-text-muted)]">{r.milk_fat_pct ?? '—'}</td>
                  <td className="text-right p-2 text-[var(--aif-text-muted)]">{r.milk_price_ct_kg ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {records.length === 0 && !showForm && (
        <p className="text-xs text-[var(--aif-text-muted)] text-center py-4">{t('herd.milkNoData')}</p>
      )}
    </div>
  );
}

// ─── Feed Tab ─────────────────────────────────────────────────
function FeedTab({ herdId, feedData, token, currency, t, onSaved }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newComp, setNewComp] = useState({ feed_name: '', quantity_kg_per_day: '', cost_per_kg: '', source: 'own' });
  const [saving, setSaving] = useState(false);

  const ration = feedData?.ration;
  const calc = feedData?.calculation;
  const isRef = feedData?.is_reference;
  const components = ration?.components || [];
  const availableComponents = feedData?.available_components || [];

  const handleSaveRation = async (updatedComponents) => {
    if (!token || !herdId) return;
    setSaving(true);
    try {
      // TODO(5b): activate concurrency — herd is 3WM
      await apiClient.patch(`/api/v1/aif/farm/herds/${herdId}/feed`, { ration_name: ration?.ration_name || t('herd.feedRation'), components: updatedComponents }, {
        entityType: 'herd', queueOnOffline: true,
      });
      onSaved?.();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
    setShowAddForm(false);
  };

  const handleAddComponent = () => {
    if (!newComp.feed_name || !newComp.quantity_kg_per_day) return;
    const updated = [...components, {
      feed_name: newComp.feed_name,
      quantity_kg_per_day: parseFloat(newComp.quantity_kg_per_day),
      cost_per_kg: newComp.cost_per_kg ? parseFloat(newComp.cost_per_kg) : null,
      source: newComp.source,
    }];
    handleSaveRation(updated);
    setNewComp({ feed_name: '', quantity_kg_per_day: '', cost_per_kg: '', source: 'own' });
  };

  const handleRemoveComponent = (idx) => {
    const updated = components.filter((_, i) => i !== idx);
    handleSaveRation(updated);
  };

  const CATEGORY_COLORS = { roughage: 'text-green-400', concentrate: 'text-amber-400', mineral: 'text-purple-400', supplement: 'text-blue-400' };

  return (
    <div className="space-y-4">
      {/* Ration header */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">
            {isRef ? t('herd.feedRefRation') : (ration?.ration_name || t('herd.feedRation'))}
          </h3>
          {isRef && <span className="text-[10px] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)] px-1.5 py-0.5 rounded">KTBL</span>}
        </div>

        {calc && (
          <div className="grid grid-cols-3 gap-2 mb-3 text-center">
            <div>
              <div className="text-sm font-bold text-[var(--aif-color-primary)]">{calc.total_cost_per_day?.toLocaleString(undefined, {minimumFractionDigits:2})}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedCostPerDay')} {currency}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-[var(--aif-text-primary)]">{calc.annual_cost_per_cow?.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedCostPerYear')} {currency}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-[var(--aif-text-primary)]">{feedData?.annual_cost_total?.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedCostTotal')} {currency}</div>
            </div>
          </div>
        )}

        {/* Component list */}
        <div className="space-y-1.5">
          {(calc?.components || components).map((comp, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-medium ${CATEGORY_COLORS[comp.category || 'roughage'] || 'text-[var(--aif-text-muted)]'}`}>
                  {comp.category?.slice(0,3).toUpperCase()}
                </span>
                <span className="text-[var(--aif-text-primary)] truncate">{comp.name_de || comp.feed_name}</span>
                <span className="text-[var(--aif-text-muted)] text-[10px]">{comp.quantity_kg_per_day} kg/Tag</span>
                {comp.cost_source === 'user_input' && <SourceBadge source="user_input" variant="compact" />}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[var(--aif-text-primary)]">{comp.cost_per_day?.toFixed(2)} {currency}</span>
                {!isRef && (
                  <button onClick={() => handleRemoveComponent(i)} className="text-red-400 text-[10px] hover:text-red-300">✕</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add component form */}
      <button
        onClick={() => setShowAddForm(!showAddForm)}
        className="w-full py-2 rounded-xl text-xs font-medium border border-dashed border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)] hover:bg-[var(--aif-color-primary)]/5"
      >
        + {t('herd.feedAdd')}
      </button>

      {showAddForm && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2">
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedComponent')}</label>
            <select value={newComp.feed_name} onChange={e => setNewComp({...newComp, feed_name: e.target.value})}
              className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
              <option value="">-- {t('form.select')} --</option>
              {availableComponents.map(c => <option key={c.name} value={c.name}>{c.name_de} ({c.source_type})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedKgDay')}</label>
              <input type="number" step="0.5" placeholder={t('herd.feedKgPlaceholder')}
                value={newComp.quantity_kg_per_day} onChange={e => setNewComp({...newComp, quantity_kg_per_day: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.feedCostPerKg')}</label>
              <input type="number" step="0.01" placeholder={t('herd.feedOptional')}
                value={newComp.cost_per_kg} onChange={e => setNewComp({...newComp, cost_per_kg: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddComponent} disabled={saving || !newComp.feed_name}
              className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium disabled:opacity-50">
              {saving ? '...' : t('herd.feedSave')}
            </button>
            <button onClick={() => setShowAddForm(false)}
              className="px-4 py-2 rounded-xl border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-muted)]">
              {t('finance.cancelEdit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Health Tab ───────────────────────────────────────────────
function HealthTab({ herdId, herdDetail, token, currency, t, onSaved }) {
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (field, value) => {
    if (!token || !herdId || !value) return;
    setSaving(true);
    try {
      // TODO(5b): activate concurrency — herd is 3WM
      await apiClient.patch(`/api/v1/aif/farm/herds/${herdId}`, { [field]: parseFloat(value) }, {
        entityType: 'herd', queueOnOffline: true,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved?.();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
    setEditing(e => ({ ...e, [field]: false }));
  };

  const metrics = [
    { field: 'annual_vet_cost_total',       label: t('herd.healthVetCost'),          unit: currency, target: null, value: herdDetail?.annual_vet_cost_total },
    { field: 'annual_breeding_cost_total',   label: t('herd.healthBreedingCost'),     unit: currency, target: null, value: herdDetail?.annual_breeding_cost_total },
    { field: 'avg_calving_interval_days',    label: t('herd.healthCalvingInterval'),  unit: t('herd.healthDays'), target: '365–400', value: herdDetail?.avg_calving_interval_days },
    { field: 'avg_insemination_index',       label: t('herd.healthInseminationIndex'),unit: '', target: '1.5–2.0', value: herdDetail?.avg_insemination_index },
    { field: 'replacement_rate_pct',         label: t('herd.healthReplacementRate'),  unit: '%', target: '25–35%', value: herdDetail?.replacement_rate_pct },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('herd.healthTitle')}</h3>
        {saved && <span className="text-[10px] text-green-400">{t('finance.overridesSaved')}</span>}
      </div>
      {metrics.map(m => (
        <div key={m.field} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-[var(--aif-text-primary)] font-medium">{m.label}</div>
              {m.target && <div className="text-[10px] text-[var(--aif-text-muted)]">{t('herd.healthTarget')}: {m.target}</div>}
            </div>
            <div className="flex items-center gap-2">
              {editing[m.field] ? (
                <input
                  autoFocus type="number" step="0.1"
                  defaultValue={m.value || ''}
                  onBlur={e => handleSave(m.field, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSave(m.field, e.target.value); if (e.key === 'Escape') setEditing(ed => ({...ed, [m.field]: false})); }}
                  className="w-24 text-right px-2 py-1 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-color-primary)] text-xs text-[var(--aif-text-primary)] outline-none"
                />
              ) : (
                <button onClick={() => setEditing(e => ({...e, [m.field]: true}))}
                  className="group flex items-center gap-1 text-right">
                  <span className={`text-sm font-bold ${m.value ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-muted)]'}`}>
                    {m.value ? `${m.value} ${m.unit}` : `— ${t('herd.healthRecord')}`}
                  </span>
                  <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--aif-color-primary)]">✎</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Economics Tab ────────────────────────────────────────────
function EconomicsTab({ herdEntry, currency, t }) {
  if (!herdEntry) {
    return <div className="text-center py-8 text-xs text-[var(--aif-text-muted)]">{t('herd.economicsNoData')}</div>;
  }

  const pa = herdEntry.per_animal || {};
  const sources = pa.sources || {};
  const costs = pa.cost_breakdown || {};
  const rev = pa.revenue_breakdown || {};
  const userPct = herdEntry.user_data_pct || 0;
  const mp = herdEntry.milk_performance;

  return (
    <div className="space-y-4">
      {/* User data percentage */}
      {userPct > 0 ? (
        <div className="rounded-xl bg-[var(--aif-color-primary)]/10 border border-[var(--aif-color-primary)]/30 px-3 py-2 text-xs text-[var(--aif-color-primary)] flex items-center gap-2">
          <span>✏️</span>
          <span>{t('herd.economicsUserDataPct', { pct: userPct })}</span>
        </div>
      ) : (
        <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] px-3 py-2 text-xs text-[var(--aif-text-muted)] flex items-center gap-2">
          <span>📊</span>
          <span>{t('herd.economicsRefData')}</span>
        </div>
      )}

      {/* Milk performance summary */}
      {mp?.annual_yield_kg && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3 text-xs">
          <div className="font-semibold text-blue-400 uppercase text-[10px] mb-1">🥛 {t('herd.milkTitle')}</div>
          <div className="flex justify-between">
            <span className="text-[var(--aif-text-muted)]">{t('herd.economicsAnnualYieldPerCow')}</span>
            <span className="text-[var(--aif-text-primary)]">{mp.annual_yield_kg?.toLocaleString()} kg <SourceBadge source={mp.source} variant="compact" /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--aif-text-muted)]">{t('finance.priceLabel')}</span>
            <span className="text-[var(--aif-text-primary)]">{mp.price_ct_kg} ct/kg <SourceBadge source={mp.source} variant="compact" /></span>
          </div>
        </div>
      )}

      {/* Revenue breakdown */}
      <div className="text-xs space-y-1.5">
        <div className="font-semibold text-green-400 uppercase text-[10px]">{t('herd.economicsRevPerCowYear')}</div>
        {Object.entries(rev).map(([key, val]) => (
          val > 0 ? (
            <div key={key} className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">{key}</span>
              <span className="text-[var(--aif-text-primary)]">
                {val?.toLocaleString()} {currency}
                {key === 'milk' && <SourceBadge source={sources.milk_revenue || 'reference'} variant="compact" />}
              </span>
            </div>
          ) : null
        ))}
        <div className="flex justify-between font-semibold border-t border-[var(--aif-border-default)] pt-1">
          <span className="text-green-400">{t('herd.economicsTotalRevenue')}</span>
          <span className="text-green-400">{pa.revenue?.toLocaleString()} {currency}</span>
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="text-xs space-y-1.5">
        <div className="font-semibold text-red-400 uppercase text-[10px]">{t('herd.economicsCostPerCowYear')}</div>
        {[
          ['feed', t('herd.economicsFeed'), sources.feed_cost],
          ['vet', t('herd.economicsVet'), sources.vet_cost],
          ['breeding', t('herd.economicsBreeding'), sources.breeding_cost],
          ['bedding', t('herd.economicsBedding'), 'reference'],
          ['other', t('finance.misc'), 'reference'],
        ].map(([key, label, src]) => (
          costs[key] > 0 ? (
            <div key={key} className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">{label} <SourceBadge source={src || 'reference'} variant="compact" /></span>
              <span className="text-[var(--aif-text-primary)]">{costs[key]?.toLocaleString()} {currency}</span>
            </div>
          ) : null
        ))}
        <div className="flex justify-between font-semibold border-t border-[var(--aif-border-default)] pt-1">
          <span className="text-red-400">{t('herd.economicsTotalCosts')}</span>
          <span className="text-red-400">{pa.variable_costs?.toLocaleString()} {currency}</span>
        </div>
      </div>

      {/* DB I */}
      <div className="rounded-xl bg-[var(--aif-color-primary)]/10 border border-[var(--aif-color-primary)]/30 p-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold text-[var(--aif-text-primary)]">= {t('finance.dbI')}/{t('herd.perCow')}</span>
          <span className="text-lg font-bold text-[var(--aif-color-primary)]">{pa.db_i?.toLocaleString()} {currency}</span>
        </div>
        <div className="flex justify-between items-center mt-1 text-xs text-[var(--aif-text-muted)]">
          <span>{t('finance.total')} ({herdEntry.head_count} {t('herd.animals')})</span>
          <span className="text-[var(--aif-color-primary)]">{herdEntry.total?.db_i?.toLocaleString()} {currency}</span>
        </div>
      </div>
    </div>
  );
}

// ─── HerdDetail (main) ───────────────────────────────────────
export default function HerdDetail({ token, finance }) {
  const { t } = useTranslation();
  const [herds, setHerds] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedHerdId, setSelectedHerdId] = useState(null);
  const [herdDetail, setHerdDetail] = useState(null);
  const [milkData, setMilkData] = useState(null);
  const [feedData, setFeedData] = useState(null);
  const [milkSummary, setMilkSummary] = useState(null);
  const [savingMetrics, setSavingMetrics] = useState(false);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/farm/herds')
      .then(d => {
        setHerds(d);
        if (d?.herds?.length > 0 && !selectedHerdId) {
          setSelectedHerdId(d.herds[0].id);
        }
      }).catch(() => {});
  }, [token]);

  // Load detail, milk, feed when selected herd changes
  useEffect(() => {
    if (!token || !selectedHerdId) return;
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}`).then(setHerdDetail).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/milk`).then(setMilkData).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/milk/summary`).then(setMilkSummary).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/feed`).then(setFeedData).catch(() => {});
  }, [token, selectedHerdId]);

  const livestock = finance?.livestock;
  const feed = finance?.feed_self_sufficiency;
  const manure = finance?.manure_balance;
  const currency = livestock?.currency || 'EUR';

  const refreshHerd = () => {
    if (!token || !selectedHerdId) return;
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}`).then(setHerdDetail).catch(() => {});
  };

  const refreshMilk = () => {
    if (!token || !selectedHerdId) return;
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/milk`).then(setMilkData).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/milk/summary`).then(setMilkSummary).catch(() => {});
  };

  const refreshFeed = () => {
    if (!token || !selectedHerdId) return;
    apiClient.get(`/api/v1/aif/farm/herds/${selectedHerdId}/feed`).then(setFeedData).catch(() => {});
  };

  if (!herds?.herds?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-4xl mb-4">🐄</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('dashboard.herd')}</h3>
        <p className="text-xs text-[var(--aif-text-muted)] max-w-[250px]">{t('herd.noHerds')}</p>
      </div>
    );
  }

  const TABS = [
    { key: 'overview',   label: t('herd.herdTabOverview'),    icon: '🐄' },
    { key: 'milk',       label: t('herd.herdTabMilk'),        icon: '🥛' },
    { key: 'feed',       label: t('herd.herdTabFeed'),        icon: '🌾' },
    { key: 'health',     label: t('herd.herdTabHealth'),      icon: '🩺' },
    { key: 'economics',  label: t('herd.herdTabEconomics'),   icon: '💶' },
  ];

  const selectedLivestockEntry = livestock?.per_herd?.find(h => h.herd_id === selectedHerdId);

  return (
    <div className="space-y-4">
      {/* Herd selector (if multiple herds) */}
      {herds.herds.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {herds.herds.map(h => (
            <button
              key={h.id}
              onClick={() => setSelectedHerdId(h.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                selectedHerdId === h.id
                  ? 'bg-[var(--aif-color-primary)] text-white border-[var(--aif-color-primary)]'
                  : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] border-[var(--aif-border-default)]'
              }`}
            >
              {h.species_name} ({h.head_count})
            </button>
          ))}
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-medium border transition-all ${
              activeTab === tab.key
                ? 'bg-[var(--aif-color-primary)]/20 border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)]'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border-[var(--aif-border-default)]'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-3">
          <div className="text-center py-2">
            <div className="text-2xl font-bold text-[var(--aif-color-primary)]">{herds.total_animals} {t('herd.animals')}</div>
            <div className="text-xs text-[var(--aif-text-muted)]">{herds.count} {t('herd.herdsCount')}</div>
          </div>
          {herds.herds.map((h, i) => {
            const lh = livestock?.per_herd?.find(lh => lh.herd_id === h.id);
            return (
              <div key={i} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{h.species_name}</span>
                    {h.breed && <span className="text-xs text-[var(--aif-text-muted)] ml-2">{h.breed}</span>}
                    {h.organic_certified && <span className="text-[10px] text-green-400 ml-2">{t('herd.organic')}</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[var(--aif-text-primary)]">{h.head_count}</div>
                    {lh && <div className="text-[10px] text-[var(--aif-color-primary)]">DB I: {lh.total?.db_i?.toLocaleString()} {currency}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-[10px] text-[var(--aif-text-muted)] mt-1">
                  {h.housing_type && <span className="px-1.5 py-0.5 rounded bg-[var(--aif-bg-elevated)]">{h.housing_type}</span>}
                  {h.has_milk_data && <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">🥛 {t('herd.milkData')}</span>}
                  {h.has_feed_ration && <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400">🌾 {t('herd.feedRation')}</span>}
                  {h.has_performance_metrics && <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">📊 {t('herd.metrics')}</span>}
                  {lh?.user_data_pct > 0 && <span className="px-1.5 py-0.5 rounded bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)]">✏️ {lh.user_data_pct}% {t('herd.ownData')}</span>}
                </div>
              </div>
            );
          })}

          {/* Feed self-sufficiency */}
          {feed && feed.demand_t_dm > 0 && (
            <div className={`rounded-xl border p-3 ${feed.status === 'ok' ? 'border-green-500/30 bg-green-500/5' : feed.status === 'warning' ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
              <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('herd.feedTitle')}</h3>
              <div className="flex items-baseline gap-2">
                <span className={`text-xl font-bold ${feed.status === 'ok' ? 'text-green-400' : feed.status === 'warning' ? 'text-yellow-400' : 'text-red-400'}`}>{feed.self_sufficiency_pct}%</span>
                <span className="text-xs text-[var(--aif-text-muted)]">{t('herd.selfSufficiency')}</span>
              </div>
              <div className="text-xs text-[var(--aif-text-muted)] mt-1">
                {feed.grassland_ha} ha → {feed.production_t_dm} t TM | {t('herd.demand')}: {feed.demand_t_dm} t TM
              </div>
              {feed.shortfall_t_dm > 0 && <div className="text-xs text-orange-400 mt-1">{feed.shortfall_t_dm} t TM {t('herd.purchaseNeeded')}</div>}
            </div>
          )}

          {/* Manure balance */}
          {manure && manure.total_n_kg > 0 && (
            <div className={`rounded-xl border p-3 ${manure.status === 'ok' ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
              <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('herd.manureTitle')}</h3>
              <div className="flex items-baseline gap-2">
                <span className={`text-xl font-bold ${manure.status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{manure.n_per_ha} kg N/ha</span>
                <span className="text-xs text-[var(--aif-text-muted)]">{t('herd.manureLimit')}: {manure.limit_n_per_ha} kg N/ha</span>
              </div>
              {manure.status === 'ok' && <div className="text-xs text-green-400 mt-1">{t('herd.manureOk')}</div>}
              {manure.status === 'exceeded' && <div className="text-xs text-red-400 mt-1">{t('herd.manureExceeded')}</div>}
            </div>
          )}
        </div>
      )}

      {/* Milk Tab */}
      {activeTab === 'milk' && (
        <MilkTab
          herdId={selectedHerdId}
          milkData={milkData}
          milkSummary={milkSummary}
          herdDetail={herdDetail}
          token={token}
          currency={currency}
          t={t}
          onSaved={refreshMilk}
        />
      )}

      {/* Feed Tab */}
      {activeTab === 'feed' && (
        <FeedTab
          herdId={selectedHerdId}
          feedData={feedData}
          token={token}
          currency={currency}
          t={t}
          onSaved={refreshFeed}
        />
      )}

      {/* Health Tab */}
      {activeTab === 'health' && (
        <HealthTab
          herdId={selectedHerdId}
          herdDetail={herdDetail}
          token={token}
          currency={currency}
          t={t}
          onSaved={refreshHerd}
        />
      )}

      {/* Economics Tab */}
      {activeTab === 'economics' && (
        <EconomicsTab
          herdEntry={selectedLivestockEntry}
          currency={currency}
          t={t}
        />
      )}
    </div>
  );
}
